// PcHealth.jsx - PC Health (group INFRA): on-demand troubleshooting snapshot of a domain PC.
// Backend: backend/pcHealth.js -> SSH 10.40.1.58 -> 10.32.0.18 (wmi_probe.py, WMI/DCOM read-only).
// Every collection needs an SDP ticket number and is audit-logged. Literal English text (no i18n keys).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MonitorSmartphone, Play, RefreshCw, Search, AlertTriangle, AlertOctagon, Info, CheckCircle2, Loader2,
  Cpu, MemoryStick, HardDrive, Clock, Battery, User, Ticket, ShieldCheck, X, Trash2,
} from 'lucide-react';
import { api } from '../context/AuthContext';
import AdOverview from './AdOverview';
import useTabDeepLink from '../hooks/useTabDeepLink';

const LEVEL = {
  crit: { cls: 'bg-rose-50 text-rose-700 ring-rose-200', Icon: AlertOctagon },
  warn: { cls: 'bg-amber-50 text-amber-700 ring-amber-200', Icon: AlertTriangle },
  info: { cls: 'bg-sky-50 text-sky-700 ring-sky-200', Icon: Info },
};
const STATUS = {
  queued:  'bg-slate-100 text-slate-600 ring-slate-200',
  running: 'bg-blue-50 text-blue-700 ring-blue-200',
  done:    'bg-emerald-50 text-emerald-700 ring-emerald-200',
  error:   'bg-rose-50 text-rose-700 ring-rose-200',
};
const CATS = {
  hardware: 'Hardware (WHEA)', disk: 'Disk / NTFS', crash: 'BSOD', power: 'Power loss', memory: 'Low memory',
  display: 'Display driver', service: 'Service', network: 'Network / domain', app: 'App crash / hang', other: 'Other',
};
const MEDIA = { 3: 'HDD', 4: 'SSD', 5: 'SCM' };
const BUS = { 7: 'USB', 8: 'RAID', 11: 'SATA', 17: 'NVMe', 10: 'SAS' };
const HEALTH = { 0: ['Healthy', 'text-emerald-700'], 1: ['Warning', 'text-amber-700'], 2: ['Unhealthy', 'text-rose-700'] };

const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : null; };
const gb = (b) => (n(b) == null ? '-' : (n(b) / 1073741824).toFixed(1) + ' GB');
function wmiTime(s) {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:\.\d+)?([+-])(\d{3})$/.exec(String(s || ''));
  if (!m) return null;
  const off = (m[7] === '-' ? -1 : 1) * Number(m[8]);
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) - off * 60000);
}
const fmtDT = (d) => (d ? d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const sqlDT = (s) => (s ? new Date(String(s).replace(' ', 'T') + 'Z') : null);   // SQLite CURRENT_TIMESTAMP (UTC)
function ago(d) {
  if (!d) return '-';
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return Math.max(1, Math.round(s / 60)) + ' min';
  if (s < 86400 * 2) return Math.round(s / 3600) + ' h';
  return Math.round(s / 86400) + ' days';
}
const errMsg = (e) => e?.response?.data?.error || e?.message || String(e);

function Chip({ level, text }) {
  const L = LEVEL[level] || LEVEL.info;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${L.cls}`}>
    <L.Icon size={12} />{text}
  </span>;
}
function StatusBadge({ s }) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${STATUS[s] || STATUS.queued}`}>
    {s === 'running' && <Loader2 size={11} className="animate-spin" />}{s}
  </span>;
}
function Bar({ pct, warn = 80, crit = 90 }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const c = p >= crit ? 'bg-rose-500' : p >= warn ? 'bg-amber-500' : 'bg-emerald-500';
  return <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${c}`} style={{ width: p + '%' }} /></div>;
}
function Tile({ Icon, label, value, sub, pct, warn, crit }) {
  return <div className="rounded-xl border border-slate-200 p-3 space-y-1.5">
    <div className="flex items-center gap-1.5 text-xs text-slate-500"><Icon size={13} />{label}</div>
    <div className="text-lg font-semibold text-slate-800 leading-tight">{value}</div>
    {pct != null && <Bar pct={pct} warn={warn} crit={crit} />}
    {sub && <div className="text-xs text-slate-500 truncate" title={sub}>{sub}</div>}
  </div>;
}
function Section({ title, right, children }) {
  return <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 min-w-0">
    <div className="flex items-center gap-2"><div className="font-semibold text-slate-800 text-sm">{title}</div><div className="ml-auto">{right}</div></div>
    {children}
  </div>;
}

function ProcTable({ rows, paths, sortKey }) {
  if (!Array.isArray(rows) || !rows.length) return <div className="text-xs text-slate-400">No data</div>;
  return <div className="overflow-x-auto"><table className="w-full text-xs">
    <thead><tr className="text-left text-slate-500 border-b border-slate-100">
      <th className="py-1.5 pr-2 font-medium">Process</th><th className="py-1.5 pr-2 font-medium text-right">PID</th>
      <th className={`py-1.5 pr-2 font-medium text-right ${sortKey === 'cpu' ? 'text-slate-800' : ''}`}>CPU %</th>
      <th className={`py-1.5 font-medium text-right ${sortKey === 'mem' ? 'text-slate-800' : ''}`}>Memory</th>
    </tr></thead>
    <tbody>{rows.map((p) => <tr key={p.pid + p.name} className="border-b border-slate-50">
      <td className="py-1 pr-2 max-w-0 w-full"><div className="truncate text-slate-700" title={(paths && paths[String(p.pid)]) || p.name}>{p.name}</div></td>
      <td className="py-1 pr-2 text-right text-slate-400 tabular-nums">{p.pid}</td>
      <td className="py-1 pr-2 text-right tabular-nums">{p.cpu_pct}</td>
      <td className="py-1 text-right tabular-nums whitespace-nowrap">{p.mem_mb >= 1024 ? (p.mem_mb / 1024).toFixed(1) + ' GB' : p.mem_mb + ' MB'}</td>
    </tr>)}</tbody>
  </table></div>;
}

function Events({ items, err }) {
  const [cat, setCat] = useState('');
  const [open, setOpen] = useState(null);
  const counts = useMemo(() => {
    const c = {};
    for (const e of items) c[e.category || 'other'] = (c[e.category || 'other'] || 0) + 1;
    return c;
  }, [items]);
  const rows = items.filter((e) => !cat || (e.category || 'other') === cat);
  return <Section title={`Important events (${items.length})`}
    right={<div className="flex flex-wrap gap-1 justify-end">
      <button onClick={() => setCat('')} className={`px-2 py-0.5 rounded-lg text-xs ${!cat ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>All</button>
      {Object.keys(counts).sort((a, b) => counts[b] - counts[a]).map((k) =>
        <button key={k} onClick={() => setCat(k)} className={`px-2 py-0.5 rounded-lg text-xs ${cat === k ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>
          {CATS[k] || k} <span className="opacity-60">{counts[k]}</span></button>)}
    </div>}>
    {err && <div className="text-xs text-amber-700">Partial event read: {err}</div>}
    {!rows.length ? <div className="text-xs text-slate-400">No error/warning events in the selected window.</div> :
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto"><table className="w-full text-xs">
        <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500 border-b border-slate-100">
          <th className="py-1.5 pr-2 font-medium whitespace-nowrap">Time</th><th className="py-1.5 pr-2 font-medium">Level</th>
          <th className="py-1.5 pr-2 font-medium">Source</th><th className="py-1.5 pr-2 font-medium text-right">ID</th><th className="py-1.5 font-medium">Message</th>
        </tr></thead>
        <tbody>{rows.map((e, i) => {
          const isOpen = open === i;
          return <tr key={i} className="border-b border-slate-50 align-top cursor-pointer hover:bg-slate-50" onClick={() => setOpen(isOpen ? null : i)}>
            <td className="py-1 pr-2 whitespace-nowrap text-slate-500">{fmtDT(wmiTime(e.TimeGenerated))}</td>
            <td className={`py-1 pr-2 ${e.Type === 'Error' ? 'text-rose-600' : 'text-amber-600'}`}>{e.Type}</td>
            <td className="py-1 pr-2 whitespace-nowrap text-slate-700">{String(e.SourceName || '').replace('Microsoft-Windows-', '')}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{e.EventCode}</td>
            <td className="py-1 text-slate-600 max-w-0 w-full">
              <div className={isOpen ? 'whitespace-pre-wrap break-words' : 'truncate'}>{e.Message || '-'}</div></td>
          </tr>;
        })}</tbody>
      </table></div>}
  </Section>;
}

function Result({ run }) {
  const d = run.data || {};
  const os = (Array.isArray(d.os) && d.os[0]) || {};
  const sys = (Array.isArray(d.system) && d.system[0]) || {};
  const bios = (Array.isArray(d.bios) && d.bios[0]) || {};
  const cpu = (Array.isArray(d.cpu) && d.cpu[0]) || {};
  const disks = Array.isArray(d.disks) ? d.disks : [];
  const pr = d.processes && !d.processes.error ? d.processes : {};
  const st = d.storage && !d.storage.error ? d.storage : {};
  const bat = Array.isArray(d.battery) && d.battery[0];
  const dp = (Array.isArray(d.disk_perf) && d.disk_perf[0]) || {};
  const evItems = Array.isArray(d.events) ? d.events : (d.events && d.events.items) || [];
  const evErr = d.events && !Array.isArray(d.events) ? (d.events.errors || [d.events.error]).filter(Boolean).join('; ') : '';
  const boot = wmiTime(os.LastBootUpTime);
  const totK = n(os.TotalVisibleMemorySize), freeK = n(os.FreePhysicalMemory);
  const ramPct = totK ? 100 * (1 - freeK / totK) : null;
  const sysDrive = disks.find((x) => x.DeviceID === 'C:') || disks[0];
  const cFreePct = sysDrive && n(sysDrive.Size) ? 100 * n(sysDrive.FreeSpace) / n(sysDrive.Size) : null;

  return <div className="space-y-4">
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-slate-800">{run.hostname || run.target}</div>
          <div className="text-xs text-slate-500">{run.ip}{sys.Manufacturer ? ` · ${sys.Manufacturer} ${sys.Model || ''}` : ''}{bios.SerialNumber ? ` · S/N ${bios.SerialNumber}` : ''}</div>
          <div className="text-xs text-slate-500">{os.Caption} {os.BuildNumber ? `(build ${os.BuildNumber})` : ''}</div>
        </div>
        <div className="text-xs text-slate-600 space-y-0.5">
          <div className="flex items-center gap-1"><User size={12} className="text-slate-400" />Signed in: <b className="font-medium">{sys.UserName || 'nobody'}</b></div>
          <div className="flex items-center gap-1"><Ticket size={12} className="text-slate-400" />{run.ticket ? `Ticket #${run.ticket}` : 'No ticket'}{run.ticket_subject ? ` - ${run.ticket_subject}` : ''}</div>
          <div className="text-slate-400">Collected {fmtDT(sqlDT(run.finished_at || run.created_at))} by {run.username} · {run.elapsed_sec} s · window {d.days || 7} days</div>
        </div>
      </div>
      {run.flags && run.flags.length > 0
        ? <div className="flex flex-wrap gap-1.5">{run.flags.map((f, i) => <Chip key={i} level={f.level} text={f.text} />)}</div>
        : <div className="flex items-center gap-1.5 text-xs text-emerald-700"><CheckCircle2 size={14} />No issues flagged automatically</div>}
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Tile Icon={Cpu} label="CPU load" value={cpu.LoadPercentage != null ? cpu.LoadPercentage + '%' : '-'} pct={n(cpu.LoadPercentage)} warn={70} crit={90}
        sub={`${cpu.Name || ''}${sys.NumberOfLogicalProcessors ? ` · ${sys.NumberOfLogicalProcessors} threads` : ''}`} />
      <Tile Icon={MemoryStick} label="Memory" value={ramPct != null ? ramPct.toFixed(0) + '%' : '-'} pct={ramPct}
        sub={totK ? `${((totK - freeK) / 1048576).toFixed(1)} of ${(totK / 1048576).toFixed(1)} GB used` : ''} />
      <Tile Icon={HardDrive} label={`Free on ${sysDrive ? sysDrive.DeviceID : 'C:'}`} value={sysDrive ? gb(sysDrive.FreeSpace) : '-'}
        pct={cFreePct != null ? 100 - cFreePct : null} warn={85} crit={92}
        sub={sysDrive ? `${cFreePct.toFixed(0)}% free of ${gb(sysDrive.Size)}` : ''} />
      <Tile Icon={Clock} label="Uptime" value={boot ? ago(boot) : '-'} sub={boot ? `Last boot ${fmtDT(boot)}` : ''} />
    </div>

    <div className="grid lg:grid-cols-2 gap-4">
      <Section title={`Top processes by CPU${pr.sample_sec ? ` (avg over ${pr.sample_sec} s)` : ''}`}>
        {d.processes && d.processes.error ? <div className="text-xs text-rose-600">{d.processes.error}</div> :
          <ProcTable rows={pr.top_cpu} paths={d.proc_paths} sortKey="cpu" />}
      </Section>
      <Section title={`Top processes by memory${pr.count ? ` · ${pr.count} running` : ''}`}>
        <ProcTable rows={pr.top_mem} paths={d.proc_paths} sortKey="mem" />
      </Section>
    </div>

    <div className="grid lg:grid-cols-2 gap-4">
      <Section title="Volumes">
        <div className="space-y-2">{disks.map((k) => {
          const used = n(k.Size) ? 100 * (1 - n(k.FreeSpace) / n(k.Size)) : 0;
          return <div key={k.DeviceID} className="space-y-1">
            <div className="flex text-xs"><span className="font-medium text-slate-700">{k.DeviceID} {k.VolumeName || ''}</span>
              <span className="ml-auto text-slate-500">{gb(k.FreeSpace)} free of {gb(k.Size)} · {k.FileSystem}</span></div>
            <Bar pct={used} warn={85} crit={92} />
          </div>;
        })}{!disks.length && <div className="text-xs text-slate-400">No data</div>}</div>
      </Section>
      <Section title="Hardware health">
        <div className="space-y-2 text-xs">
          {(Array.isArray(st.disks) ? st.disks : []).map((k, i) => {
            const h = HEALTH[n(k.HealthStatus)] || [String(k.HealthStatus ?? '-'), 'text-slate-600'];
            const rel = (Array.isArray(st.reliability) ? st.reliability : []).find((r) => String(r.DeviceId) === String(k.DeviceId));
            return <div key={i} className="flex items-start gap-2">
              <HardDrive size={14} className="text-slate-400 mt-0.5" />
              <div className="min-w-0">
                <div className="text-slate-700">{k.FriendlyName} <span className="text-slate-400">{MEDIA[n(k.MediaType)] || ''} {BUS[n(k.BusType)] || ''} · {gb(k.Size)}</span></div>
                <div className={h[1]}>{h[0]}{rel ? ` · ${rel.Temperature ?? '-'}°C · wear ${rel.Wear ?? '-'}% · read err ${rel.ReadErrorsTotal ?? 0} · power-on ${rel.PowerOnHours ?? '-'} h` : ''}</div>
              </div>
            </div>;
          })}
          {d.storage && d.storage.error && <div className="text-amber-700">Storage: {d.storage.error}</div>}
          {bat && <div className="flex items-start gap-2">
            <Battery size={14} className="text-slate-400 mt-0.5" />
            <div>Battery {bat.EstimatedChargeRemaining ?? '-'}% charged{bat.health_pct != null ? ` · capacity ${bat.health_pct}% of design (${bat.full_mwh} / ${bat.design_mwh} mWh)` : ''}</div>
          </div>}
          {!bat && <div className="text-slate-400">No battery (desktop)</div>}
          {bios.SMBIOSBIOSVersion && <div className="text-slate-400">BIOS {bios.SMBIOSBIOSVersion}</div>}
        </div>
      </Section>
    </div>

    <BootTimeline boot={d.boot} gp={d.gp} os={os} />
    <GroupPolicy gp={d.gp} />
    <HyperV hv={d.hyperv} />
    <Events items={evItems} err={evErr} />
  </div>;
}

// ── Boot & logon timeline (System log sejak boot) ────────────────────────────────────
const GP_DONE = { 1500: 'Computer policy (no changes)', 1502: 'Computer policy applied', 1501: 'User policy (no changes)', 1503: 'User policy applied' };
const dcShort = (s) => String(s || '').replace(/^\\+/, '').split('.')[0];
function BootTimeline({ boot, gp, os }) {
  if (!boot) return null;
  if (boot.error) return <Section title="Boot & logon"><div className="text-xs text-amber-700">{boot.error}</div></Section>;
  const ev = (boot.events || []).map((e) => ({ ...e, t: wmiTime(e.TimeGenerated), code: Number(e.EventCode), ins: e.InsertionStrings || [] }));
  const lastBoot = wmiTime(boot.last_boot || (os && os.LastBootUpTime));
  const osStart = ev.find((e) => e.code === 12 && e.SourceName.endsWith('Kernel-General'));
  const names = {};
  for (const u of (gp && gp.users) || []) if (u.sid && u.session && u.session.targetName) names[u.sid.toUpperCase()] = u.session.targetName;
  const sidName = (sid) => names[String(sid || '').toUpperCase()] || sid;
  const firstComp = ev.find((e) => [1500, 1502].includes(e.code));
  const firstLogon = ev.find((e) => e.code === 7001);
  const firstUserGp = firstLogon ? ev.find((e) => [1501, 1503].includes(e.code) && e.t >= firstLogon.t) : null;
  const sec = (a, b) => (a && b ? Math.round((b.getTime() - a.getTime()) / 1000) : null);
  const gpRows = ev.filter((e) => GP_DONE[e.code]);
  const problems = ev.filter((e) => e.SourceName === 'Microsoft-Windows-GroupPolicy' && (e.Type === 'Error' || e.Type === 'Warning'));
  const other = ev.filter((e) => (e.code === 41 && /Kernel-Power/.test(e.SourceName)) || (e.code === 6008) || (/User Profiles/.test(e.SourceName)));
  const Step = ({ label, t, sub, tone = 'slate' }) => <div className="flex items-start gap-2 text-xs">
    <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${tone === 'amber' ? 'bg-amber-500' : tone === 'rose' ? 'bg-rose-500' : tone === 'emerald' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    <div className="w-36 shrink-0 text-slate-500 tabular-nums">{t ? fmtDT(t) : '-'}</div>
    <div className="min-w-0"><div className="text-slate-700">{label}</div>{sub && <div className="text-slate-500">{sub}</div>}</div></div>;
  const toneMs = (ms) => (ms >= 60000 ? 'rose' : ms >= 30000 ? 'amber' : 'emerald');
  return <Section title="Boot & logon" right={<span className="text-xs text-slate-500">since last boot {fmtDT(lastBoot)} ({ago(lastBoot)} ago)</span>}>
    <div className="grid md:grid-cols-3 gap-3">
      <div className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] text-slate-500">Boot → computer policy done</div>
        <div className="text-lg font-semibold text-slate-800">{sec(osStart ? osStart.t : lastBoot, firstComp && firstComp.t) ?? '-'}{firstComp ? ' s' : ''}</div>
        <div className="text-[11px] text-slate-500">{firstComp ? `GP processing ${Math.round(Number(firstComp.ins[3] || 0) / 1000)} s · DC ${dcShort(firstComp.ins[4])} · ${firstComp.ins[5] || '?'} GPOs` : 'no computer policy event since boot'}</div></div>
      <div className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] text-slate-500">First logon after boot</div>
        <div className="text-lg font-semibold text-slate-800">{firstLogon ? fmtDT(firstLogon.t).split(', ').pop() : '-'}</div>
        <div className="text-[11px] text-slate-500 truncate">{firstLogon ? `${sidName(firstLogon.ins[1])} · ${sec(osStart ? osStart.t : lastBoot, firstLogon.t)} s after boot` : 'nobody logged on since boot'}</div></div>
      <div className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] text-slate-500">Logon → user policy done</div>
        <div className="text-lg font-semibold text-slate-800">{sec(firstLogon && firstLogon.t, firstUserGp && firstUserGp.t) ?? '-'}{firstUserGp ? ' s' : ''}</div>
        <div className="text-[11px] text-slate-500">{firstUserGp ? `GP processing ${Math.round(Number(firstUserGp.ins[3] || 0) / 1000)} s · DC ${dcShort(firstUserGp.ins[4])}` : '-'}</div></div>
    </div>
    <div className="space-y-1.5">
      {lastBoot && <Step label="Windows started" t={osStart ? osStart.t : lastBoot} />}
      {gpRows.slice(-12).map((e, i) => { const ms = Number(e.ins[3] || 0); return <Step key={i} label={GP_DONE[e.code]} t={e.t} tone={toneMs(ms)}
        sub={`${(ms / 1000).toFixed(1)} s · DC ${dcShort(e.ins[4])} · ${e.ins[5] || '?'} GPOs`} />; })}
      {ev.filter((e) => e.code === 7001 || e.code === 7002).slice(-8).map((e, i) => <Step key={'l' + i} label={e.code === 7001 ? 'User logon' : 'User logoff'} t={e.t} sub={sidName(e.ins[1])} />)}
    </div>
    {(problems.length > 0 || other.length > 0) && <div className="space-y-1">
      <div className="text-xs font-medium text-slate-700">Problems since boot</div>
      {[...problems, ...other].slice(-15).map((e, i) => <div key={i} className="text-xs flex gap-2">
        <span className={`shrink-0 px-1 rounded ${e.Type === 'Error' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{e.code}</span>
        <span className="text-slate-500 shrink-0 w-28">{fmtDT(e.t)}</span><span className="text-slate-700 break-words min-w-0">{e.Message}</span></div>)}
    </div>}
    <div className="text-[11px] text-slate-400">GP processing time comes from Group Policy events 1500-1503 (System log).</div>
  </Section>;
}

// ── Group Policy yang benar-benar diterapkan (RSoP) ──────────────────────────────────
const CSE = { '{00000000-0000-0000-0000-000000000000}': 'Group Policy core', '{35378EAC-683F-11D2-A89A-00C04FBBCFA2}': 'Administrative Templates (Registry)',
  '{827D319E-6EAC-11D2-A4EA-00C04F79F83A}': 'Security settings', '{42B5FAAE-6536-11D2-AE5A-0000F87571E3}': 'Scripts', '{17D89FEC-5C44-4972-B12D-241CAEF74509}': 'Local Users and Groups',
  '{B087BE9D-ED37-454F-AF9C-04291E351182}': 'Registry (Preferences)', '{5794DAFD-BE60-433F-88A2-1A31939AC01F}': 'Drive Maps', '{C6DC5466-785A-11D2-84D0-00C04FB169F7}': 'Software Installation',
  '{25537BA6-77A8-11D2-9B6C-0000F8080861}': 'Folder Redirection', '{BC75B1ED-5833-4858-9BB8-CBF0B166DF9D}': 'Printers', '{AADCED64-746C-4633-A97C-D61349046527}': 'Scheduled Tasks',
  '{91FBB303-0CD5-4055-BF42-E512A681B325}': 'Services (Preferences)', '{6232C319-91AC-4931-9385-E70C2B099F0E}': 'Folders', '{7150F9BF-48AD-4DA4-A49C-29EF4A8369BA}': 'Files',
  '{0ACDD40C-75AC-47AB-BAA0-BF6DE7E7FE63}': 'Wireless', '{F3CCC681-B74C-4060-9F26-CD84525DCA2A}': 'Advanced Audit Policy', '{A3F3E39B-5D83-4940-B954-28315B82F0A8}': 'Folder Options',
  '{E62688F0-25FD-4C90-BFF5-F508B9D2E31F}': 'Power Options', '{E47248BA-94CC-49C4-BBB5-9EB7F05183D0}': 'Internet Settings', '{3A0DBA37-F8B2-4356-83DE-3E90BD5C261F}': 'Network Options',
  '{728EE579-943C-4519-9EF7-AB56765798ED}': 'Data Sources', '{1A6364EB-776B-4120-ADE1-B63A406A76B5}': 'Devices', '{5C935941-A954-4F7C-B507-885941ECE5C4}': 'Internet Settings',
  '{3610EDA5-77EF-11D2-8DC5-00C04FA31A66}': 'Disk Quota', '{B1BE8D72-6EAC-11D2-A4EA-00C04F79F83A}': 'EFS Recovery', '{E437BC1C-AA7D-11D2-A382-00C04F991E27}': 'IP Security',
  '{FB2CA36D-0B40-4307-821B-A13B252DE56C}': 'Policy-based QoS', '{CF7639F3-ABA2-41DB-97F2-81E2C5DBFC5D}': 'Internet Explorer Maintenance', '{CDEAFC3D-948D-49DD-AB12-E578BA4AF7AA}': 'TCP/IP' };
const somLabel = (s) => (s === 'Local' ? 'Local' : String(s || '').split(',').filter((p) => !/^DC=/i.test(p)).map((p) => p.replace(/^(OU|CN)=/i, '')).reverse().join(' / ') || 'Domain root');
function whyNot(g) {
  if (g.denied) return 'Security filtering (no Apply permission)';
  if (g.filter_allowed === false) return g.wmi_filter ? 'WMI filter returned false' : 'Filtered out';
  if (g.gpo_enabled === false) return 'GPO disabled';
  if (g.link_enabled === false) return 'Link disabled';
  if (g.som === 'Local') return 'Local policy (empty)';
  return 'Not applied (empty or no settings for this side)';
}
function GpList({ r, title }) {
  if (!r) return null;
  if (r.error) return <div className="text-xs text-amber-700">{title}: {r.error}</div>;
  const ext = Array.isArray(r.extensions) ? r.extensions : [];
  const extErr = ext.filter((x) => x.error && x.error !== 0);
  return <div className="space-y-2 min-w-0">
    <div className="text-xs font-medium text-slate-700">{title}{r.session && r.session.targetName ? <span className="font-normal text-slate-500"> · {r.session.targetName}</span> : null}
      {r.session && r.session.Site && <span className="font-normal text-slate-500"> · site {r.session.Site}</span>}
      {r.session && String(r.session.slowLink).toLowerCase() === 'true' && <span className="ml-1 px-1 rounded bg-amber-100 text-amber-700 font-normal">slow link</span>}</div>
    {r.session && r.session.SOM && <div className="text-[11px] text-slate-500">Location: {somLabel(r.session.SOM)}</div>}
    <table className="w-full text-xs"><thead><tr className="text-left text-slate-500 border-b"><th className="py-1 pr-2 w-6">#</th><th className="pr-2">Applied GPO</th><th className="pr-2">Linked at</th><th></th></tr></thead>
      <tbody>{(r.applied || []).map((g, i) => <tr key={i} className="border-b border-slate-50">
        <td className="py-1 pr-2 text-slate-400 tabular-nums">{g.applied_order}</td>
        <td className="pr-2 text-slate-700">{g.name}</td>
        <td className="pr-2 text-slate-500">{somLabel(g.som)}</td>
        <td className="whitespace-nowrap">{g.enforced && <span className="px-1 rounded bg-rose-50 text-rose-700">enforced</span>}{g.wmi_filter && <span className="ml-1 px-1 rounded bg-slate-100 text-slate-600">WMI filter</span>}</td>
      </tr>)}
      {!(r.applied || []).length && <tr><td colSpan={4} className="py-2 text-slate-400">No domain GPO applied.</td></tr>}</tbody></table>
    {(r.not_applied || []).filter((g) => g.som !== 'Local').length > 0 && <div className="text-xs space-y-0.5">
      <div className="text-slate-500">Linked in scope but not applied:</div>
      {(r.not_applied || []).filter((g) => g.som !== 'Local').map((g, i) => <div key={i} className="text-slate-500"><span className="text-slate-700">{g.name}</span> · {somLabel(g.som)} · <span className="text-amber-700">{whyNot(g)}</span></div>)}
    </div>}
    {ext.length > 0 && <div className="flex flex-wrap gap-1">{ext.map((x, i) => { const nm = CSE[String(x.extensionGuid || '').toUpperCase()] || x.extensionGuid; const bad = x.error && x.error !== 0;
      return <span key={i} title={bad ? `error ${x.error}` : 'OK'} className={`text-[10px] px-1.5 py-0.5 rounded ${bad ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{nm}{bad ? ` · err ${x.error}` : ''}</span>; })}</div>}
    {extErr.length > 0 && <div className="text-[11px] text-rose-600">{extErr.length} policy extension(s) reported an error on the last run.</div>}
  </div>;
}
function GroupPolicy({ gp }) {
  if (!gp) return null;
  const users = (gp.users || []).filter((u) => !u.error || u.session);
  return <Section title="Group Policy applied" right={<span className="text-xs text-slate-500">Resultant Set of Policy recorded by Windows on the last refresh</span>}>
    <GpList r={gp.computer} title="Computer" />
    {users.map((u, i) => <div key={i} className="pt-2 border-t border-slate-100"><GpList r={u} title="User" /></div>)}
    {!users.length && <div className="text-[11px] text-slate-400">No user policy data (nobody has logged on, or RSoP logging is disabled).</div>}
  </Section>;
}

const VM_STATE = { 2: ['Running', 'bg-emerald-100 text-emerald-700'], 3: ['Off', 'bg-slate-100 text-slate-500'], 6: ['Saved', 'bg-sky-100 text-sky-700'],
  9: ['Paused', 'bg-amber-100 text-amber-700'], 32768: ['Paused', 'bg-amber-100 text-amber-700'], 32769: ['Saved', 'bg-sky-100 text-sky-700'] };
function HyperV({ hv }) {
  if (!hv) return null;
  if (hv.error) return <Section title="Hyper-V guest VMs"><div className="text-xs text-amber-700">{hv.error}</div></Section>;
  const vms = hv.vms || [];
  const run = vms.filter((v) => v.state === 2);
  const memRun = run.reduce((a, v) => a + (v.memory_mb || 0), 0);
  return <Section title={`Hyper-V guest VMs (${vms.length})`}
    right={<span className="text-xs text-slate-500">{run.length} running · {(memRun / 1024).toFixed(1)} GB RAM assigned to running VMs</span>}>
    {!vms.length ? <div className="text-xs text-slate-400">Hyper-V host with no virtual machines.</div> :
    <div className="overflow-x-auto"><table className="w-full text-xs">
      <thead><tr className="text-left text-slate-500 border-b">
        <th className="py-1 pr-2">VM</th><th className="pr-2">State</th><th className="pr-2 text-right">vCPU</th><th className="pr-2 text-right">Memory</th>
        <th className="pr-2">Uptime</th><th className="pr-2">Guest hostname</th><th className="pr-2">Guest IP</th><th className="pr-2">Guest OS</th></tr></thead>
      <tbody>{vms.map((v) => { const s = VM_STATE[v.state] || [`State ${v.state}`, 'bg-slate-100 text-slate-500']; return (
        <tr key={v.guid} className="border-b border-slate-100">
          <td className="py-1 pr-2 font-medium text-slate-800">{v.name}</td>
          <td className="pr-2"><span className={`px-1.5 py-0.5 rounded ${s[1]}`}>{s[0]}</span>{v.health && v.health !== 5 ? <span className="ml-1 text-rose-600">health {v.health}</span> : null}</td>
          <td className="pr-2 text-right">{v.vcpu ?? '-'}</td>
          <td className="pr-2 text-right">{v.memory_mb ? (v.memory_mb >= 1024 ? (v.memory_mb / 1024).toFixed(1) + ' GB' : v.memory_mb + ' MB') : '-'}</td>
          <td className="pr-2">{v.state === 2 && v.uptime_sec ? (v.uptime_sec >= 86400 ? Math.floor(v.uptime_sec / 86400) + ' d' : Math.round(v.uptime_sec / 3600) + ' h') : '-'}</td>
          <td className="pr-2">{v.guest_fqdn || '-'}</td>
          <td className="pr-2">{v.guest_ip || '-'}</td>
          <td className="pr-2 text-slate-500">{v.guest_os || (v.state === 2 ? 'no integration data' : '-')}</td>
        </tr>); })}</tbody>
    </table></div>}
  </Section>;
}

// ── Riwayat per PC: perbandingan dengan collect sebelumnya ───────────────────────────
const pcKey = (r) => String((r && (r.hostname || r.target)) || '').toUpperCase();
function evItemsOf(d) { return Array.isArray(d && d.events) ? d.events : (d && d.events && d.events.items) || []; }
function Delta({ v, unit = '', invert = false, digits = 1 }) {
  if (v == null || !Number.isFinite(v) || Math.abs(v) < 0.05) return <span className="text-slate-400">no change</span>;
  const good = invert ? v < 0 : v > 0;
  return <span className={good ? 'text-emerald-700' : 'text-rose-700'}>{v > 0 ? '+' : ''}{v.toFixed(digits)}{unit}</span>;
}
function Compare({ run, prev }) {
  const d = run.data || {}, p = prev.data || {};
  const os1 = (d.os || [])[0] || {}, os0 = (p.os || [])[0] || {};
  const ram = (o) => (n(o.TotalVisibleMemorySize) ? 100 * (1 - n(o.FreePhysicalMemory) / n(o.TotalVisibleMemorySize)) : null);
  const disks = (d.disks || []).map((k) => {
    const o = (p.disks || []).find((x) => x.DeviceID === k.DeviceID);
    return { id: k.DeviceID, now: n(k.FreeSpace) / 1073741824, delta: o ? (n(k.FreeSpace) - n(o.FreeSpace)) / 1073741824 : null };
  });
  const b1 = wmiTime(os1.LastBootUpTime), b0 = wmiTime(os0.LastBootUpTime);
  const rebooted = b1 && b0 && Math.abs(b1 - b0) > 60000;
  const since = new Date(p.collected_at || prev.finished_at || prev.created_at);
  const newEv = evItemsOf(d).filter((e) => { const t = wmiTime(e.TimeGenerated); return t && t > since; });
  const cnt = {};
  for (const e of newEv) cnt[e.category || 'other'] = (cnt[e.category || 'other'] || 0) + 1;
  const fk = (f) => f.code + '|' + f.level;
  const f0 = new Set((prev.flags || []).map(fk)), f1 = new Set((run.flags || []).map(fk));
  const added = (run.flags || []).filter((f) => !f0.has(fk(f)));
  const gone = (prev.flags || []).filter((f) => !f1.has(fk(f)));
  const bat1 = (d.battery || [])[0], bat0 = (p.battery || [])[0];
  return <div className="bg-white rounded-2xl border border-indigo-100 p-4 space-y-2">
    <div className="text-sm font-semibold text-slate-800">Changes since previous collection <span className="font-normal text-slate-500">({fmtDT(sqlDT(prev.finished_at || prev.created_at))} · {prev.ticket ? '#' + prev.ticket : 'no ticket'})</span></div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 text-xs">
      {disks.map((k) => <div key={k.id} className="flex justify-between"><span className="text-slate-500">Free on {k.id}</span><span>{k.now.toFixed(1)} GB · <Delta v={k.delta} unit=" GB" /></span></div>)}
      <div className="flex justify-between"><span className="text-slate-500">RAM used</span><span>{ram(os1) != null ? ram(os1).toFixed(0) + '%' : '-'} · <Delta v={ram(os1) != null && ram(os0) != null ? ram(os1) - ram(os0) : null} unit=" pt" invert digits={0} /></span></div>
      <div className="flex justify-between"><span className="text-slate-500">Restarted in between</span><span className={rebooted ? 'text-emerald-700' : 'text-slate-500'}>{rebooted ? `yes (${fmtDT(b1)})` : 'no'}</span></div>
      {bat1 && bat0 && bat1.health_pct != null && <div className="flex justify-between"><span className="text-slate-500">Battery capacity</span><span>{bat1.health_pct}% · <Delta v={bat1.health_pct - (bat0.health_pct || 0)} unit=" pt" /></span></div>}
      <div className="flex justify-between"><span className="text-slate-500">New important events</span><span className={newEv.length ? 'text-amber-700' : 'text-slate-500'}>{newEv.length}{newEv.length ? ` (${Object.entries(cnt).map(([k, v]) => `${CATS[k] || k} ${v}`).join(', ')})` : ''}</span></div>
    </div>
    {(added.length > 0 || gone.length > 0) && <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {added.map((f, i) => <span key={'a' + i} className="inline-flex items-center gap-1"><span className="text-slate-400">new:</span><Chip level={f.level} text={f.text} /></span>)}
      {gone.map((f, i) => <span key={'g' + i} className="inline-flex items-center gap-1 line-through opacity-60"><Chip level={f.level} text={f.text} /></span>)}
    </div>}
  </div>;
}
function HistoryBar({ hist, cur, onPick }) {
  if (!hist || hist.length < 2) return null;
  return <div className="flex flex-wrap items-center gap-1.5 text-xs">
    <span className="text-slate-500 mr-1">History ({hist.length}):</span>
    {hist.map((r, i) => <button key={r.id} onClick={() => onPick(r.id)}
      className={`px-2 py-1 rounded-lg ring-1 ${r.id === cur ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}
      title={`${r.ticket ? '#' + r.ticket : 'no ticket'} · ${r.username}${r.status !== 'done' ? ' · ' + r.status : ''}`}>
      {i === 0 ? 'Latest · ' : ''}{fmtDT(sqlDT(r.created_at))}{r.status === 'error' ? ' ✕' : ''}</button>)}
  </div>;
}

function CollectTab({ prefill }) {
  const [target, setTarget] = useState('');
  const [ticket, setTicket] = useState('');
  const [ticketInfo, setTicketInfo] = useState(null);
  const [note, setNote] = useState('');
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [runs, setRuns] = useState([]);
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState(() => {
    try { return Number(new URLSearchParams(window.location.search).get('run')) || null; } catch (_) { return null; }
  });
  const [run, setRun] = useState(null);
  const [tick, setTick] = useState(0);
  const pollRef = useRef(null);
  const [prev, setPrev] = useState(null);

  // Prefill dari tab AD Overview (tombol "Check" pada daftar komputer).
  useEffect(() => {
    if (prefill && prefill.target) { setTarget(prefill.target); setErr(''); }
  }, [prefill]);

  const loadRuns = useCallback(() => {
    api.get('/api/pc-health/runs', { params: { q, limit: 200 } }).then((r) => setRuns(r.data.rows || [])).catch((e) => setErr(errMsg(e)));
  }, [q]);
  useEffect(() => { const t = setTimeout(loadRuns, 250); return () => clearTimeout(t); }, [loadRuns]);

  // Load selected run; poll while it is still running.
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (!selId) { setRun(null); return undefined; }
    let alive = true;
    const load = () => api.get(`/api/pc-health/run/${selId}`).then((r) => {
      if (!alive) return;
      setRun(r.data);
      if (r.data.status === 'queued' || r.data.status === 'running') pollRef.current = setTimeout(load, 3000);
      else loadRuns();
    }).catch((e) => alive && setErr(errMsg(e)));
    load();
    try { const u = new URL(window.location.href); u.searchParams.set('run', selId); window.history.replaceState(null, '', u); } catch (_) {}
    return () => { alive = false; clearTimeout(pollRef.current); };
  }, [selId]);

  const running = run && (run.status === 'queued' || run.status === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => {
    const t = ticket.replace(/^#/, '').trim();
    if (!/^\d+$/.test(t)) { setTicketInfo(null); return undefined; }
    const h = setTimeout(() => api.get(`/api/pc-health/ticket/${t}`).then((r) => setTicketInfo(r.data.ticket || false)).catch(() => setTicketInfo(null)), 400);
    return () => clearTimeout(h);
  }, [ticket]);

  const collect = async (e) => {
    e && e.preventDefault();
    setErr('');
    if (!target.trim()) { setErr('Hostname/IP is required.'); return; }
    if (!ticket.trim() && note.trim().length < 10) { setErr('Enter an SDP ticket number or a reason (min. 10 characters) in Note.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/pc-health/collect', { target: target.trim(), ticket: ticket.trim(), note: note.trim(), days });
      setSelId(r.data.id);
      loadRuns();
    } catch (ex) { setErr(errMsg(ex)); }
    finally { setBusy(false); }
  };
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of runs) { const k = pcKey(r); if (!m.has(k)) m.set(k, []); m.get(k).push(r); }
    return [...m.entries()].map(([k, list]) => ({ k, list }));
  }, [runs]);
  const hist = useMemo(() => (run ? ((groups.find((g) => g.k === pcKey(run)) || {}).list || []) : []), [groups, run]);
  const prevRow = run && run.status === 'done' ? hist.find((r) => r.id < run.id && r.status === 'done' && r.has_data) : null;
  const prevId = prevRow ? prevRow.id : null;
  useEffect(() => {
    setPrev(null);
    if (!prevId) return undefined;
    let alive = true;
    api.get(`/api/pc-health/run/${prevId}`).then((r) => { if (alive) setPrev(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [prevId]);
  const rerun = (r) => { setTarget(r.target); setTicket(r.ticket); setNote(r.note || ''); };

  const elapsed = running ? Math.max(0, Math.round((Date.now() - (sqlDT(run.created_at)?.getTime() || Date.now())) / 1000)) : 0;
  void tick;

  return <div className="space-y-4 min-w-0">
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-xl bg-indigo-50"><MonitorSmartphone size={20} className="text-indigo-600" /></div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-800">PC Health</div>
          <div className="text-xs text-slate-500">On-demand troubleshooting snapshot of a domain PC: running processes, memory, disk space, disk/battery health and important Windows events.
            Read-only WMI queries from the on-prem collector 10.32.0.18 - nothing is installed or run on the user's PC.</div>
        </div>
      </div>
      <form onSubmit={collect} className="grid gap-3 md:grid-cols-[1.2fr_1fr_0.6fr_1.4fr_auto] items-end">
        <label className="text-xs text-slate-600 space-y-1 block">Hostname or IP
          <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. WUPIMIDJKTKNZ24 or 10.32.0.25"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </label>
        <label className="text-xs text-slate-600 space-y-1 block">SDP ticket no.
          <input value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="e.g. 12345"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </label>
        <label className="text-xs text-slate-600 space-y-1 block">Events
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm bg-white">
            {[3, 7, 14, 30].map((x) => <option key={x} value={x}>{x} days</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600 space-y-1 block">Note / reason <span className="text-slate-400">(required if no ticket)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. user reports PC slow after login" maxLength={300}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </label>
        <button type="submit" disabled={busy}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}Collect
        </button>
      </form>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {ticketInfo && <span className="text-emerald-700">#{ticketInfo.display_id}: {ticketInfo.subject} ({ticketInfo.status}{ticketInfo.requester_name ? `, ${ticketInfo.requester_name}` : ''})</span>}
        {ticketInfo === false && <span className="text-amber-700">Ticket not found in synced SDP data - collection is still allowed.</span>}
        <span className="flex items-center gap-1 text-slate-400"><ShieldCheck size={12} />Every collection is recorded with your name, the PC and the ticket or reason.</span>
      </div>
      {err && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 rounded-lg px-3 py-2">
        <AlertOctagon size={16} className="mt-0.5 shrink-0" /><span className="flex-1">{err}</span>
        <button onClick={() => setErr('')}><X size={14} /></button></div>}
    </div>

    <div className="grid xl:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
      <div className="bg-white rounded-2xl border border-slate-200 p-3 space-y-2 min-w-0">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PC, IP, ticket, user"
              className="w-full rounded-lg border border-slate-200 pl-8 pr-2 py-1.5 text-xs" />
          </div>
          <button onClick={loadRuns} className="p-1.5 rounded-lg hover:bg-slate-100" title="Refresh"><RefreshCw size={14} className="text-slate-500" /></button>
        </div>
        <div className="text-xs text-slate-400 px-1">History · {groups.length} PCs · {runs.length} collections</div>
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100">
          {groups.map(({ k, list }) => {
            const r = list[0];
            const active = run && pcKey(run) === k;
            const crit = (r.flags || []).filter((f) => f.level === 'crit').length;
            const warn = (r.flags || []).filter((f) => f.level === 'warn').length;
            return <button key={k} onClick={() => setSelId(r.id)}
              className={`w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 ${active ? 'bg-indigo-50' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-slate-800 truncate">{r.hostname || r.target}</span>
                {list.length > 1 && <span className="text-[10px] px-1.5 rounded bg-slate-100 text-slate-500">{list.length} runs</span>}
                <span className="ml-auto"><StatusBadge s={r.status} /></span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className={r.ticket ? '' : 'px-1 rounded bg-amber-100 text-amber-700'}>{r.ticket ? '#' + r.ticket : 'no ticket'}</span><span>{fmtDT(sqlDT(r.created_at))}</span><span className="truncate">{r.username}</span>
                <span className="ml-auto flex gap-1">
                  {crit > 0 && <span className="px-1.5 rounded bg-rose-100 text-rose-700">{crit}</span>}
                  {warn > 0 && <span className="px-1.5 rounded bg-amber-100 text-amber-700">{warn}</span>}
                </span>
              </div>
            </button>;
          })}
          {!runs.length && <div className="text-xs text-slate-400 p-2">No collections yet.</div>}
        </div>
      </div>

      <div className="min-w-0">
        {!run && <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400">
          Enter a hostname and a ticket number or reason, then press Collect. Results appear here in about 45-60 seconds.</div>}
        {run && running && <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-2">
          <Loader2 size={28} className="animate-spin text-indigo-500 mx-auto" />
          <div className="text-sm text-slate-700">Collecting from <b>{run.target}</b> · {elapsed} s</div>
          <div className="text-xs text-slate-400">Processes are sampled twice to measure CPU, so this usually takes 45-60 seconds.</div>
        </div>}
        {run && run.status === 'error' && <div className="bg-white rounded-2xl border border-rose-200 p-5 space-y-2">
          <div className="flex items-center gap-2 text-rose-700 font-medium"><AlertOctagon size={18} />Collection failed - {run.hostname || run.target}{run.ip ? ` (${run.ip})` : ''}</div>
          <div className="text-sm text-slate-700">{run.error}</div>
          <div className="text-xs text-slate-400">{run.ticket ? `Ticket #${run.ticket}` : 'No ticket'} · {fmtDT(sqlDT(run.created_at))} · {run.username}</div>
          <button onClick={() => rerun(run)} className="text-xs text-indigo-600 hover:underline">Fill the form to retry</button>
        </div>}
        {run && run.status === 'error' && <div className="mt-3"><SdpCard sdp={run.sdp} cmp={null} /></div>}
        {run && !running && <div className="flex justify-end mb-2">
          <button onClick={() => {
            if (!window.confirm(`Delete collection #${run.id} (${run.hostname || run.target})? This is recorded in the audit log.`)) return;
            api.delete(`/api/pc-health/run/${run.id}`).then(() => { setSelId(null); loadRuns(); }).catch((e) => setErr(errMsg(e)));
          }} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"><Trash2 size={13} />Delete this collection</button>
        </div>}
        {run && <div className="mb-3"><HistoryBar hist={hist} cur={run.id} onPick={setSelId} /></div>}
        {run && run.status === 'done' && run.data && prev && prev.data && <div className="mb-4"><Compare run={run} prev={prev} /></div>}
        {run && run.status === 'done' && run.data && <Result run={run} />}
        {run && run.status === 'done' && <div className="mt-4"><SdpCard sdp={run.sdp} cmp={run.sdp_compare} /></div>}
        {run && run.status === 'done' && !run.data && <div className="bg-white rounded-2xl border border-slate-200 p-5 text-sm text-slate-500">
          Detailed data for this collection was removed by the retention policy. The audit record is kept.</div>}
      </div>
    </div>
  </div>;
}

// ── SDP asset card (cross-check) ─────────────────────────────────────────────────
function SdpCard({ sdp, cmp }) {
  const tone = { match: 'text-emerald-700 bg-emerald-50 border-emerald-200', mismatch: 'text-rose-700 bg-rose-50 border-rose-200', differs: 'text-amber-700 bg-amber-50 border-amber-200', unknown: 'text-slate-600 bg-slate-50 border-slate-200' };
  const label = { match: 'SDP match', mismatch: 'SDP mismatch', differs: 'differs', unknown: 'not comparable' };
  if (!sdp) return <div className="bg-white rounded-2xl border border-slate-200 p-4 text-sm text-slate-500">
    <div className="font-medium text-slate-700 mb-1">SDP asset</div>Not found in SDP assets (by hostname or IP).</div>;
  const rows = [
    ['Asset', sdp.name], ['State', sdp.state], ['Assigned to', [sdp.assigned_user, sdp.assigned_user_email].filter(Boolean).join(' · ')],
    ['Department', sdp.department], ['Site / location', [sdp.site, sdp.location].filter(Boolean).join(' · ')],
    ['Product', [sdp.manufacturer, sdp.product_name].filter(Boolean).join(' ')], ['Serial / tag', [sdp.serial_number, sdp.service_tag, sdp.asset_tag].filter((v, i, a) => v && a.indexOf(v) === i).join(' · ')],
    ['OS', [sdp.os, sdp.os_build && `build ${sdp.os_build}`].filter(Boolean).join(' ')], ['Last user (SDP)', sdp.last_logged_user],
    ['IP', sdp.ws_ip_address || sdp.ip_address], ['MAC', sdp.mac_address], ['Warranty', sdp.warranty_expiry],
    ['SDP snapshot', sdp.snapshot_at],
  ].filter((r) => r[1]);
  return <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
    <div className="flex items-center gap-2">
      <div className="font-medium text-slate-700 text-sm">SDP asset</div>
      {cmp && <span className={`text-xs px-2 py-0.5 rounded-full border ${tone[cmp.status]}`}>{label[cmp.status]}</span>}
      {sdp.state && sdp.state !== 'In Use' && <span className={`text-xs px-2 py-0.5 rounded-full border ${tone.differs}`}>{sdp.state}</span>}
      <span className="text-xs text-slate-400 ml-auto">matched by {sdp.matched_by}</span>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
      {rows.map(([k, v]) => <div key={k} className="flex gap-2 min-w-0"><span className="text-slate-400 w-28 shrink-0">{k}</span><span className="text-slate-700 break-all">{v}</span></div>)}
    </div>
    {cmp && cmp.items.length > 0 && <table className="w-full text-xs">
      <thead><tr className="text-left text-slate-400"><th className="py-1 pr-2 font-normal">Field</th><th className="pr-2 font-normal">PC (collected)</th><th className="pr-2 font-normal">SDP</th><th className="font-normal"></th></tr></thead>
      <tbody>{cmp.items.map((x) => <tr key={x.field} className="border-t border-slate-100 align-top">
        <td className="py-1 pr-2 text-slate-500">{x.field}</td><td className="pr-2 text-slate-700 break-all">{x.pc || '-'}</td>
        <td className="pr-2 text-slate-700 break-all">{x.sdp || '-'}</td>
        <td><span className={`px-1.5 py-0.5 rounded border ${tone[x.status]}`}>{label[x.status] === 'SDP match' ? 'match' : label[x.status] === 'SDP mismatch' ? 'mismatch' : label[x.status]}</span></td>
      </tr>)}</tbody>
    </table>}
  </div>;
}

// ── Halaman: tab AD Overview | PC Collect ───────────────────────────────────────────
const PCH_TABS = ['ad', 'collect'];
export default function PcHealth() {
  const [tab, setTab] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (PCH_TABS.includes(sp.get('tab'))) return sp.get('tab');
      if (sp.get('run')) return 'collect';
    } catch (_) {}
    return 'ad';
  });
  useTabDeepLink('pc_health', (p) => PCH_TABS.includes(p), setTab);
  const [prefill, setPrefill] = useState(null);
  const onCollect = (name) => { setPrefill({ target: name, at: Date.now() }); setTab('collect'); };
  return <div className="space-y-4 min-w-0">
    <div className="inline-flex bg-slate-100/80 rounded-2xl p-1 gap-0.5">
      {[['ad', 'AD Overview'], ['collect', 'PC Collect']].map(([k, label]) => <button key={k} onClick={() => setTab(k)}
        className={`px-4 py-1.5 rounded-xl text-sm font-semibold ${tab === k ? 'bg-white shadow-sm ring-1 ring-slate-200 text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>{label}</button>)}
    </div>
    <div style={{ display: tab === 'ad' ? 'block' : 'none' }}><AdOverview onCollect={onCollect} /></div>
    <div style={{ display: tab === 'collect' ? 'block' : 'none' }}><CollectTab prefill={prefill} /></div>
  </div>;
}
